import Search16 from '@carbon/icons-react/es/search/16';
import { unionBy } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectSource } from 'api/project';
import { Input } from 'components/atoms/input';
import { Option } from 'components/atoms/select';
import { TaskTemplateLight } from 'data/templates/tasks';
import { useTranslate } from 'hooks/use-translation';
import { TemplateSelect } from '../search-templates';
import { Divider, SearchInput, ShowSavedTemplates, Spacer } from '../styles';

interface DrawerContentTopBarProps {
  sourceTemplates: ProjectSource[] | undefined;
  changeCurrentProjectTo: (template: TaskTemplateLight | null) => void;
  taskTemplates: TaskTemplateLight[] | undefined;
  currentShowingTemplate: TaskTemplateLight | null;
  setSearchableTemplates: (value: TaskTemplateLight[] | undefined) => void;
}

export const DrawerContentTopBar: FC<DrawerContentTopBarProps> = ({
  sourceTemplates,
  changeCurrentProjectTo,
  taskTemplates = [],
  currentShowingTemplate,
  setSearchableTemplates,
}) => {
  const translate = useTranslate();
  const templateOptions = useMemo(() => {
    const templates = unionBy(taskTemplates, 'parentId');

    return [
      {
        label: translate('projects.task_template.drawer.all_templates'),
        value: null,
      },
      ...templates.map((template) => ({
        label: template.parentName,
        value: template,
      })),
    ];
  }, [taskTemplates, translate]);

  useEffect(() => {
    return () => setSearchableTemplates(taskTemplates);
  }, [setSearchableTemplates, taskTemplates]);

  const onSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = e.target.value;

      if (searchTerm === '') {
        setSearchableTemplates(taskTemplates);

        return;
      }

      const filteredTemplates = taskTemplates?.filter((template) => {
        return (
          template.parentName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          template.templateName.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });

      setSearchableTemplates(filteredTemplates);
    },
    [setSearchableTemplates, taskTemplates],
  );

  const selected = useMemo(() => {
    return currentShowingTemplate
      ? {
          label: currentShowingTemplate.parentName,
          value: currentShowingTemplate,
        }
      : {
          label: translate('projects.task_template.drawer.all_templates'),
          value: null,
        };
  }, [currentShowingTemplate, translate]);

  return (
    <>
      <Spacer>
        <ShowSavedTemplates>
          <FormattedMessage id="projects.task_template.drawer.show_saved_task_templates" />
        </ShowSavedTemplates>
        <TemplateSelect
          options={templateOptions as unknown as Option[]}
          selected={selected as unknown as Option}
          sourceTemplates={sourceTemplates}
          onChange={(option) =>
            changeCurrentProjectTo(
              option?.value as unknown as TaskTemplateLight,
            )
          }
        />
      </Spacer>
      <Divider />
      <SearchInput>
        <Input
          type="search"
          placeholder={translate('projects.task_template.drawer.search.text')}
          onChange={onSearch}
        />
        <Search16 />
      </SearchInput>
    </>
  );
};
